import { PortalRecentTransactionsTableProps } from '@/components/portal-modules/PortalRecentTransactionsTable';
import { convertedQtyFormatterConfig, defaultColumn } from '@/config/config';
import { portalRecentTransactionsColumns } from '@/helpers/portalRecentTransactionsHelper';
import { useAppSelector } from '@/state/hooks';
import { selectTablePagination } from '@/state/reducers/tableSettingSlice';
import { DepositWithdrawalRequest } from '@/types/sso';
import {
    ColumnDef,
    filterFns,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import { getCurrency } from './useCurrency';
import { useDebouncedValue } from './useDebouncedValue';
import useLanguage from './useLanguage';
import { fuzzyGlobalFilter } from './useOpenPositionTable';

export interface PortalRecentTransactionsRow extends DepositWithdrawalRequest {}

interface UsePortalRecentTransactionsTableProps extends PortalRecentTransactionsTableProps {}

const usePortalRecentTransactionsTable = (props: UsePortalRecentTransactionsTableProps) => {
    const { mode, data } = props;

    const pagination = useAppSelector(selectTablePagination);

    const formatQty = useNumberFormatter(convertedQtyFormatterConfig);
    const formatDate = useDateFormatter();

    const lang = useLanguage();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});

    const columns: ColumnDef<PortalRecentTransactionsRow>[] = useMemo(() => {
        return portalRecentTransactionsColumns({ lang, mode, formatQty, formatDate });
    }, [lang, mode, formatQty, formatDate]);

    const formattedPortalRecentTransactionss: PortalRecentTransactionsRow[] = useMemo(() => {
        if (data) {
            return data.data.filter((item: PortalRecentTransactionsRow) => {
                if (mode === 'bank') {
                    return (
                        item.details.bankInfoId > -1 ||
                        (item.requestType === 'INTERNAL_TRANSFER' &&
                            getCurrency(item.details.digitalAssetName || item.details.currencyName).type === 'Fiat')
                    );
                } else {
                    return (
                        item.details.walletId > -1 ||
                        (item.requestType === 'INTERNAL_TRANSFER' &&
                            getCurrency(item.details.digitalAssetName || item.details.currencyName).type === 'Crypto')
                    );
                }
            });
        }
        return [];
    }, [data, mode]);

    const tableInstance = useReactTable({
        data: formattedPortalRecentTransactionss,
        columns,
        state: { globalFilter, columnSizing, sorting },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        pageCount: Math.ceil(formattedPortalRecentTransactionss.length / pagination.pageSize),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, formatDate);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getPaginationRowModel: getPaginationRowModel(),
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    return { debouncedTableInstance, columnSizing, sorting, formattedPortalRecentTransactionss, globalFilter };
};

export default usePortalRecentTransactionsTable;
